import { useMemo, useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Card, CardContent } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { Checkbox } from "../../../../components/ui/checkbox";
import { cn } from "../../../../lib/utils";
import SignalFilterContainer from "../../signals/signal-overview/components/SignalFilterContainer";
import useObjectSearchTable from "../../../../shared/table/useObjectSearchTable";
import { useWorkspaceMembers } from "../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import type { AssignmentDraft } from "./AlertsAssignmentPage";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const ASSIGN_MODE_OPTIONS = [
  { value: "least_accounts", label: "Least Accounts (Balance Load)" },
  { value: "least_revenue", label: "Least Revenue (Balance ARR)" },
  { value: "round_robin", label: "Round Robin (Rotate)" },
];

const KEY_ROLE_OPTIONS = [
  { value: "csm_id", label: "CSM" },
  { value: "ae_id", label: "Account Executive" },
  { value: "account_champion_id", label: "Account Champion" },
];

const SCOPE_OPTIONS = [
  { value: "accounts", label: "Accounts" },
  { value: "companies", label: "Companies" },
];

const DEFAULT_PREVIEW_FIELDS = ["accounts.name", "accounts.external_id", "accounts.last_seen"];

/* ------------------------------------------------------------------ */
/*  Account Preview                                                    */
/* ------------------------------------------------------------------ */

const AccountPreview = ({ workspaceId, filter }: { workspaceId: string; filter: any }) => {
  const hasPredicates = filter?.predicates?.some(
    (group: any) => group?.predicates?.some((p: any) => p?.field?.trim()),
  );

  const { rows, total, isLoading, isFetching } = useObjectSearchTable({
    workspaceId,
    entityType: "accounts",
    enabled: !!workspaceId && hasPredicates,
    initialPageSize: 10,
    initialSort: { field: "accounts.last_seen", order: "desc" },
    queryOptions: {
      filter: hasPredicates ? filter : undefined,
      selectedFields: DEFAULT_PREVIEW_FIELDS,
      fallbackSortField: "accounts.last_seen",
    },
    parseRows: (rawRows) =>
      (rawRows || []).map((record: any) => {
        const fields = record?.fields || {};
        return {
          name: fields.name || record?.name || "",
          external_id: fields.external_id || record?.id || "",
          last_seen: fields.last_seen || "",
        };
      }),
    resetPageDeps: [JSON.stringify(filter)],
  });

  if (!hasPredicates) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-center text-sm text-muted-foreground">
          Add filter conditions to preview matching accounts.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading preview...
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-sm font-medium">Matching Accounts</h3>
        <Badge
          variant="outline"
          className={cn(
            "rounded-full px-2 text-[11px]",
            total === 0 && "border-amber-300 text-amber-700",
          )}
        >
          {isFetching ? "..." : total}
        </Badge>
      </div>
      {total === 0 ? (
        <p className="text-xs text-amber-600">No accounts match these conditions.</p>
      ) : (
        <div className="overflow-hidden rounded-md border border-border text-xs">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">Name</th>
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">External ID</th>
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row: any, idx: number) => (
                <tr key={idx} className="border-b border-border last:border-0">
                  <td className="px-3 py-1.5 text-foreground">{row?.name || "\u2014"}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">{row?.external_id || "\u2014"}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {row?.last_seen ? new Date(row.last_seen).toLocaleDateString() : "\u2014"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

type Props = {
  draft: AssignmentDraft;
  saving: boolean;
  onSave: (d: AssignmentDraft) => void;
  onCancel: () => void;
};

const AssignmentEditor = ({ draft: initial, saving, onSave, onCancel }: Props) => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const workspaceId = workspace?.id ?? "";
  const [draft, setDraft] = useState<AssignmentDraft>({ ...initial });
  const [limitToUsers, setLimitToUsers] = useState(initial.user_ids.length > 0);

  const patch = (partial: Partial<AssignmentDraft>) => setDraft((d) => ({ ...d, ...partial }));

  /* --- workspace members --- */
  const { data: membersData } = useWorkspaceMembers({
    workspaceId,
    enabled: !!workspaceId,
  } as any);

  const memberOptions = useMemo(() => {
    const list: any[] = (membersData as any)?.members ?? (membersData as any)?.users ?? [];
    return list.map((m: any) => ({
      id: m.user_id || m.id,
      label: m.first_name && m.last_name ? `${m.first_name} ${m.last_name}` : m.email ?? "Unknown",
      email: m.email,
    }));
  }, [membersData]);

  /* --- filter helpers --- */
  const handleFilter = (
    field: string,
    operator: string,
    value: any,
    type: string,
    entityType: string,
    id: number,
    filterId: number,
  ) => {
    const newFilters = { ...draft.filters };
    const predicates = [...(newFilters.predicates || [])];
    if (!predicates[filterId]) predicates[filterId] = { predicates: [] };
    const group = { ...predicates[filterId] };
    const items = [...(group.predicates || [])];
    items[id] = { field, operator, value, type, entity_type: entityType };
    group.predicates = items;
    predicates[filterId] = group;
    newFilters.predicates = predicates;
    patch({ filters: newFilters });
  };

  const handleRemoveFilter = (id: number, filterId?: number) => {
    const fid = filterId ?? 0;
    const newFilters = { ...draft.filters };
    const predicates = [...(newFilters.predicates || [])];
    if (predicates[fid]) {
      const group = { ...predicates[fid] };
      const items = [...(group.predicates || [])];
      items.splice(id, 1);
      group.predicates = items;
      predicates[fid] = group;
    }
    newFilters.predicates = predicates;
    patch({ filters: newFilters });
  };

  const handleAddFilter = (filterId?: number) => {
    const fid = filterId ?? 0;
    const newFilters = { ...draft.filters };
    const predicates = [...(newFilters.predicates || [])];
    if (!predicates[fid]) predicates[fid] = { predicates: [] };
    const group = { ...predicates[fid] };
    group.predicates = [
      ...(group.predicates || []),
      { field: "", operator: "", value: "", type: "", entity_type: "column" },
    ];
    predicates[fid] = group;
    newFilters.predicates = predicates;
    patch({ filters: newFilters });
  };

  /* --- user selection --- */
  const toggleUser = (userId: string) => {
    const current = draft.user_ids;
    if (current.includes(userId)) {
      patch({ user_ids: current.filter((id) => id !== userId) });
    } else {
      patch({ user_ids: [...current, userId] });
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-semibold">{initial.id ? "Edit Assignment Rule" : "New Assignment Rule"}</h2>
      </div>

      {/* Two-column layout: form left, preview right */}
      <div className="flex gap-6">
        {/* ---- Left: Form ---- */}
        <div className="flex-1 min-w-0 space-y-6">
          {/* Name & Description */}
          <div className="space-y-3">
            <div>
              <Label>Rule Name</Label>
              <Input
                value={draft.name}
                onChange={(e) => patch({ name: e.target.value })}
                placeholder="e.g. Auto-assign CSM to new accounts"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={draft.description}
                onChange={(e) => patch({ description: e.target.value })}
                placeholder="Describe what this assignment rule does"
                className="mt-1"
                rows={2}
              />
            </div>
          </div>

          {/* Filter Conditions */}
          <div>
            <Label className="mb-2 block">Filter Conditions</Label>
            <p className="mb-3 text-xs text-muted-foreground">
              Define which accounts this rule applies to. Runs every hour automatically. Accounts are only assigned once (all-time reentry).
            </p>
            <Card>
              <CardContent className="pt-4">
                {(draft.filters?.predicates || []).map((group: any, filterId: number) => (
                  <SignalFilterContainer
                    key={filterId}
                    selectedFilters={group.predicates}
                    handleFilter={handleFilter}
                    handleRemove={handleRemoveFilter}
                    handleAdd={handleAddFilter}
                    filterId={filterId}
                    entityRelation={draft.entity_relation}
                  />
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Assignment Configuration */}
          <div className="space-y-4">
            <Label className="block text-base">Assignment Configuration</Label>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Role to Assign</Label>
                <Select value={draft.key_role_default_key} onValueChange={(v) => patch({ key_role_default_key: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KEY_ROLE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">Assignment Strategy</Label>
                <Select value={draft.assign_mode} onValueChange={(v) => patch({ assign_mode: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSIGN_MODE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Scope</Label>
                <Select value={draft.scope_entity} onValueChange={(v) => patch({ scope_entity: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCOPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">Max Assignments Per Run (optional)</Label>
                <Input
                  type="number"
                  min={1}
                  value={draft.max_accounts_per_user === "" ? "" : draft.max_accounts_per_user}
                  onChange={(e) => patch({ max_accounts_per_user: e.target.value === "" ? "" : Number(e.target.value) })}
                  placeholder="e.g. 5"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Limit to specific users */}
          <div>
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={limitToUsers}
                onCheckedChange={(checked) => {
                  setLimitToUsers(checked === true);
                  if (!checked) patch({ user_ids: [] });
                }}
                className="h-4 w-4"
              />
              <span className="text-sm">Limit assignment to specific users</span>
            </label>

            {limitToUsers && (
              <Card className="mt-3">
                <CardContent className="pt-4">
                  <p className="mb-3 text-xs text-muted-foreground">
                    Select users to distribute assignments among using the selected strategy.
                  </p>
                  <div className="max-h-[200px] space-y-1 overflow-y-auto">
                    {memberOptions.map((member) => (
                      <label key={member.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-muted">
                        <Checkbox
                          checked={draft.user_ids.includes(member.id)}
                          onCheckedChange={() => toggleUser(member.id)}
                          className="h-3.5 w-3.5"
                        />
                        <span className="text-sm">{member.label}</span>
                        <span className="text-xs text-muted-foreground">{member.email}</span>
                      </label>
                    ))}
                    {memberOptions.length === 0 && (
                      <p className="py-4 text-center text-xs text-muted-foreground">No workspace members found</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t pt-4">
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
            <Button onClick={() => onSave(draft)} disabled={saving}>
              {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {initial.id ? "Save Changes" : "Create Rule"}
            </Button>
          </div>
        </div>

        {/* ---- Right: Preview ---- */}
        <div className="w-[380px] shrink-0">
          <div className="sticky top-8 rounded-lg border border-border bg-background">
            <div className="border-b border-border px-4 py-3">
              <h3 className="text-sm font-medium">Account Preview</h3>
              <p className="text-[11px] text-muted-foreground">Accounts matching your filter conditions</p>
            </div>
            <AccountPreview workspaceId={workspaceId} filter={draft.filters} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentEditor;
